import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useData } from '../../context/DataContext';
import { homeworkAPI } from '../../services/api';
import PDFViewerModal from '../../components/PDFViewerModal';
import HomeworkModal from '../../components/HomeworkModal';
import Modal from '../../components/Modal';
import './Homework.css';

const AdminHomework = () => {
  const { t } = useTranslation();
  const { courses, studies = [], data } = useData();
  const cohorts = data?.cohorts || [];
  const [loading, setLoading] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [submissionsMap, setSubmissionsMap] = useState({});
  const [selectedStudy, setSelectedStudy] = useState('');
  const [selectedStudyYear, setSelectedStudyYear] = useState('');
  const [selectedCohort, setSelectedCohort] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedModule, setSelectedModule] = useState('');
  const [selectedInstructor, setSelectedInstructor] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showHomeworkModal, setShowHomeworkModal] = useState(false);
  const [editingHomework, setEditingHomework] = useState(null);
  const [homeworkModalLoading, setHomeworkModalLoading] = useState(false);

  // Modal states
  const [pdfModal, setPdfModal] = useState({ isOpen: false, url: '', title: '' });
  const [gradingModal, setGradingModal] = useState({ isOpen: false, submission: null, homeworkId: null });
  const [auditLogsModal, setAuditLogsModal] = useState({ isOpen: false, homeworkId: null, logs: [], loading: false });
  const [gradingForm, setGradingForm] = useState({ grade: '', feedback: '' });

  const toId = (value) => {
    if (!value) return '';
    if (typeof value === 'object') return String(value.id || value._id || '');
    return String(value);
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 1000 };
      if (selectedStudy) params.studyId = selectedStudy;
      if (selectedStudyYear) params.studyYearId = selectedStudyYear;
      if (selectedCohort) params.cohortId = selectedCohort;
      if (selectedCourse) params.courseId = selectedCourse;
      if (selectedInstructor) params.instructorId = selectedInstructor;
      const data = await homeworkAPI.getAll(params);
      setAssignments(data.homework || []);
    } catch (err) {
      console.error('Failed to load assignments', err);
    } finally {
      setLoading(false);
    }
  }, [selectedStudy, selectedStudyYear, selectedCohort, selectedCourse, selectedInstructor]);

  const studyYearOptions = useMemo(() => {
    if (!selectedStudy) return [];
    const study = (studies || []).find((item) => String(item.id || item._id) === String(selectedStudy));
    if (!study) return [];
    return (study.studyYears || [])
      .map((year) => ({
        id: toId(year),
        label: year.name || `Jaar ${year.yearNumber || year.year || ''}`
      }))
      .filter((year) => year.id)
      .sort((left, right) => left.label.localeCompare(right.label, 'nl'));
  }, [selectedStudy, studies]);

  const cohortOptions = useMemo(() => {
    if (!selectedStudy) {
      return [...cohorts].sort((left, right) => String(left.name || '').localeCompare(String(right.name || ''), 'nl'));
    }

    return cohorts
      .filter((cohort) => (cohort.studies || []).some((study) => toId(study) === String(selectedStudy)))
      .sort((left, right) => String(left.name || '').localeCompare(String(right.name || ''), 'nl'));
  }, [cohorts, selectedStudy]);

  const instructorOptions = useMemo(() => {
    const map = new Map();
    (courses || []).forEach((course) => {
      const instructor = course?.instructor;
      const instructorId = String(instructor?.id || instructor?._id || '');
      if (!instructorId) return;
      if (!map.has(instructorId)) {
        map.set(instructorId, {
          id: instructorId,
          name: instructor?.name || [instructor?.firstName, instructor?.lastName].filter(Boolean).join(' ') || 'Onbekende docent'
        });
      }
    });
    return Array.from(map.values()).sort((left, right) => left.name.localeCompare(right.name, 'nl'));
  }, [courses]);

  const fetchSubmissions = async (homeworkId) => {
    try {
      const subs = await homeworkAPI.getSubmissions(homeworkId);
      setSubmissionsMap(prev => ({ ...prev, [homeworkId]: subs }));
    } catch (err) {
      console.error('Failed to load submissions', err);
    }
  };

  const fetchAuditLogs = async (homeworkId) => {
    setAuditLogsModal(prev => ({ ...prev, loading: true }));
    try {
      const logs = await homeworkAPI.getAuditLogs(homeworkId);
      setAuditLogsModal(prev => ({ ...prev, logs, loading: false }));
    } catch (err) {
      console.error('Failed to load audit logs', err);
      setAuditLogsModal(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const courseOptions = useMemo(() => {
    let scopedCourses = [...(courses || [])];

    if (selectedStudyYear) {
      scopedCourses = scopedCourses.filter((course) => String(toId(course.studyYear)) === String(selectedStudyYear));
    }

    return scopedCourses.sort((left, right) => String(left.code || left.name || '').localeCompare(String(right.code || right.name || ''), 'nl'));
  }, [courses, selectedStudyYear]);

  const moduleOptions = useMemo(() => {
    const options = [];
    const sourceCourses = selectedCourse
      ? (courses || []).filter((course) => String(course.id || course._id) === String(selectedCourse))
      : courseOptions;

    sourceCourses.forEach((course) => {
      const moduleList = [
        ...(course.module ? [course.module] : []),
        ...(course.modules || [])
      ];

      moduleList.forEach((moduleDoc) => {
        const moduleId = String(moduleDoc?.id || moduleDoc?._id || moduleDoc || '');
        if (!moduleId) return;
        options.push({
          id: moduleId,
          label: `${moduleDoc?.code || 'MOD'} - ${moduleDoc?.name || 'Module'}`
        });
      });
    });

    const deduped = new Map();
    options.forEach((option) => {
      if (!deduped.has(option.id)) deduped.set(option.id, option);
    });
    return Array.from(deduped.values()).sort((left, right) => left.label.localeCompare(right.label, 'nl'));
  }, [courses, courseOptions, selectedCourse]);

  const filteredAssignments = useMemo(() => {
    let scoped = assignments;

    if (selectedModule) {
      scoped = scoped.filter((assignment) => {
        const courseId = assignment.course?.id || assignment.course?._id || assignment.course;
        const course = (courses || []).find((item) => String(item.id || item._id) === String(courseId));
        if (!course) return false;

        const moduleIds = [
          ...(course.module ? [course.module] : []),
          ...(course.modules || [])
        ].map((moduleDoc) => String(moduleDoc?.id || moduleDoc?._id || moduleDoc || '')).filter(Boolean);

        return moduleIds.includes(String(selectedModule));
      });
    }

    if (statusFilter !== 'all') {
      const isActive = statusFilter === 'active';
      scoped = scoped.filter((assignment) => Boolean(assignment.isActive) === isActive);
    }

    const needle = searchQuery.trim().toLowerCase();
    if (needle) {
      scoped = scoped.filter((assignment) => {
        const title = String(assignment.title || '').toLowerCase();
        const description = String(assignment.description || '').toLowerCase();
        const courseName = String(assignment.course?.name || '').toLowerCase();
        return title.includes(needle) || description.includes(needle) || courseName.includes(needle);
      });
    }

    return scoped;
  }, [assignments, courses, selectedModule, statusFilter, searchQuery]);

  const clearFilters = () => {
    setSelectedStudy('');
    setSelectedStudyYear('');
    setSelectedCohort('');
    setSelectedCourse('');
    setSelectedInstructor('');
    setSelectedModule('');
    setSearchQuery('');
    setStatusFilter('all');
  };

  const handleToggleActive = async (hw) => {
    try {
      await homeworkAPI.update(hw.id, { isActive: !hw.isActive });
      fetchAssignments();
    } catch (err) {
      console.error('Failed to update assignment status', err);
    }
  };

  const handleDeleteHomework = async (hw) => {
    if (!window.confirm(t('homework.confirmDelete') || 'Weet je zeker dat je dit huiswerk wilt verwijderen?')) {
      return;
    }

    try {
      await homeworkAPI.delete(hw.id);
      setSubmissionsMap((prev) => {
        const next = { ...prev };
        delete next[hw.id];
        return next;
      });
      if (auditLogsModal.homeworkId === hw.id) {
        closeAuditLogsModal();
      }
      fetchAssignments();
    } catch (err) {
      console.error('Failed to delete homework', err);
      alert(t('common.error'));
    }
  };

  const handleHomeworkSubmit = async (data) => {
    setHomeworkModalLoading(true);
    try {
      if (editingHomework) {
        await homeworkAPI.update(editingHomework.id, data, data.file);
      } else {
        await homeworkAPI.create(data, data.file);
      }
      setShowHomeworkModal(false);
      setEditingHomework(null);
      fetchAssignments();
    } catch (err) {
      console.error('Failed to save homework:', err);
      throw err;
    } finally {
      setHomeworkModalLoading(false);
    }
  };

  // PDF Modal handlers
  const revokePdfUrl = (url) => {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  };

  const openPdfModal = (url, title) => {
    setPdfModal((prev) => {
      revokePdfUrl(prev.url);
      return { isOpen: true, url, title };
    });
  };

  const closePdfModal = () => {
    setPdfModal((prev) => {
      revokePdfUrl(prev.url);
      return { isOpen: false, url: '', title: '' };
    });
  };

  const downloadBlob = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'download';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  // These files sit behind authenticated endpoints, so a plain <a href> can't
  // fetch them (no Authorization header on link navigation). Fetch the bytes
  // ourselves and hand off a blob URL / synthetic download instead.
  const handleViewAttachment = async (hw) => {
    try {
      const { blob } = await homeworkAPI.fetchAttachment(hw.id, 'view');
      openPdfModal(URL.createObjectURL(blob), hw.title);
    } catch (err) {
      console.error('Failed to load attachment', err);
      alert(err.message || t('common.error'));
    }
  };

  const handleDownloadAttachment = async (hw) => {
    try {
      const { blob, filename } = await homeworkAPI.fetchAttachment(hw.id, 'download');
      downloadBlob(blob, filename || hw.attachment?.originalName);
    } catch (err) {
      console.error('Failed to download attachment', err);
      alert(err.message || t('common.error'));
    }
  };

  const handleViewSubmission = async (hw, sub) => {
    try {
      const { blob } = await homeworkAPI.fetchSubmissionFile(hw.id, sub.id, 'view');
      openPdfModal(URL.createObjectURL(blob), `${sub.student.name} - ${hw.title}`);
    } catch (err) {
      console.error('Failed to load submission', err);
      alert(err.message || t('common.error'));
    }
  };

  const handleDownloadSubmission = async (hw, sub) => {
    try {
      const { blob, filename } = await homeworkAPI.fetchSubmissionFile(hw.id, sub.id, 'download');
      downloadBlob(blob, filename || sub.file?.originalName);
    } catch (err) {
      console.error('Failed to download submission', err);
      alert(err.message || t('common.error'));
    }
  };

  const handleDownloadSubmissionsZip = async (hw) => {
    try {
      const { blob, filename } = await homeworkAPI.fetchSubmissionsZip(hw.id);
      downloadBlob(blob, filename);
    } catch (err) {
      console.error('Failed to download submissions zip', err);
      alert(err.message || t('common.error'));
    }
  };

  // Grading handlers
  const openGradingModal = (submission, homeworkId) => {
    setGradingForm({ grade: submission.grade || '', feedback: submission.feedback || '' });
    setGradingModal({ isOpen: true, submission, homeworkId });
  };

  const closeGradingModal = () => {
    setGradingModal({ isOpen: false, submission: null, homeworkId: null });
    setGradingForm({ grade: '', feedback: '' });
  };

  const handleGradeSubmit = async (e) => {
    e.preventDefault();
    if (!gradingModal.submission || !gradingModal.homeworkId) return;

    const grade = parseFloat(gradingForm.grade);
    if (isNaN(grade) || grade < 0 || grade > 10) {
      alert(t('homework.gradeError'));
      return;
    }

    try {
      await homeworkAPI.gradeSubmission(
        gradingModal.homeworkId,
        gradingModal.submission.id,
        grade,
        gradingForm.feedback
      );
      closeGradingModal();
      fetchSubmissions(gradingModal.homeworkId);
    } catch (err) {
      console.error('Failed to grade submission', err);
      alert(t('common.error'));
    }
  };

  // Audit logs handlers
  const openAuditLogsModal = (homeworkId) => {
    setAuditLogsModal({ isOpen: true, homeworkId, logs: [], loading: true });
    fetchAuditLogs(homeworkId);
  };

  const closeAuditLogsModal = () => {
    setAuditLogsModal({ isOpen: false, homeworkId: null, logs: [], loading: false });
  };

  const getActionLabel = (action) => {
    const labels = {
      'download_submission': t('homework.auditDownload'),
      'view_submission': t('homework.auditView'),
      'grade_submission': t('homework.auditGrade')
    };
    return labels[action] || action;
  };

  return (
    <div className="admin-homework-page">
      <div className="page-header">
        <h1>{t('nav.homework')}</h1>
        <button 
          className="btn-primary" 
          onClick={() => {
            setEditingHomework(null);
            setShowHomeworkModal(true);
          }}
        >
          {t('homework.createAssignment') || 'Create Assignment'}
        </button>
      </div>

      {/* Homework Creation/Editing Modal */}
      <HomeworkModal
        isOpen={showHomeworkModal}
        onClose={() => {
          setShowHomeworkModal(false);
          setEditingHomework(null);
        }}
        onSubmit={handleHomeworkSubmit}
        initialData={editingHomework}
        loading={homeworkModalLoading}
      />

      <section className="assignment-list">
        <h2>{t('homework.assignments') || 'Assignments'}</h2>
        <div className="filter-panel">
          <div className="filter-grid">
            <div className="filter-item">
              <label>Zoeken</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Zoek op titel, beschrijving of vak"
              />
            </div>
            <div className="filter-item">
              <label>Studie</label>
              <select
                value={selectedStudy}
                onChange={(e) => {
                  setSelectedStudy(e.target.value);
                  setSelectedStudyYear('');
                  setSelectedCohort('');
                  setSelectedCourse('');
                  setSelectedModule('');
                }}
              >
                <option value="">Alle studies</option>
                {studies.map((study) => (
                  <option key={study.id || study._id} value={study.id || study._id}>{study.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <label>Studiejaar</label>
              <select
                value={selectedStudyYear}
                onChange={(e) => {
                  setSelectedStudyYear(e.target.value);
                  setSelectedCourse('');
                  setSelectedModule('');
                }}
                disabled={!selectedStudy}
              >
                <option value="">Alle studiejaren</option>
                {studyYearOptions.map((year) => (
                  <option key={year.id} value={year.id}>{year.label}</option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <label>Cohort</label>
              <select value={selectedCohort} onChange={(e) => setSelectedCohort(e.target.value)}>
                <option value="">Alle cohorts</option>
                {cohortOptions.map((cohort) => (
                  <option key={cohort.id || cohort._id} value={cohort.id || cohort._id}>{cohort.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <label>Vak</label>
              <select value={selectedCourse} onChange={(e) => { setSelectedCourse(e.target.value); setSelectedModule(''); }}>
                <option value="">Alle vakken</option>
                {courseOptions.map(c => (
                  <option key={c.id || c._id} value={c.id || c._id}>{c.code ? `${c.code} - ${c.name}` : c.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <label>Docent</label>
              <select value={selectedInstructor} onChange={(e) => setSelectedInstructor(e.target.value)}>
                <option value="">Alle docenten</option>
                {instructorOptions.map((instructor) => (
                  <option key={instructor.id} value={instructor.id}>{instructor.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <label>Module</label>
              <select value={selectedModule} onChange={(e) => setSelectedModule(e.target.value)}>
                <option value="">Alle modules</option>
                {moduleOptions.map((moduleOption) => (
                  <option key={moduleOption.id} value={moduleOption.id}>{moduleOption.label}</option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <label>Status</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">Alles</option>
                <option value="active">Actief</option>
                <option value="inactive">Inactief</option>
              </select>
            </div>
          </div>
          <div className="filter-actions">
            <button type="button" className="btn-secondary" onClick={clearFilters}>Filters wissen</button>
          </div>
        </div>

        {loading && <div className="loading">{t('common.loading')}</div>}

        {filteredAssignments.map(hw => {
          const submissions = submissionsMap[hw.id] || [];
          return (
            <div key={hw.id} className="assignment-card">
              <div className="assignment-header">
                <div>
                  <h3>{hw.title}</h3>
                  <p className="assignment-meta">
                      {t('homework.dueDate')}: {formatDateTime(hw.dueDate)}
                    {' • '}
                    {hw.isActive ? t('homework.active') : t('homework.inactive')}
                  </p>
                    <p className="assignment-meta">
                      Studie: {hw.study?.name || '-'} {' • '} Cohort: {hw.cohort?.name || '-'}
                    </p>
                </div>
                <button className="toggle-btn" onClick={() => handleToggleActive(hw)}>
                  {hw.isActive ? t('homework.deactivate') : t('homework.activate')}
                </button>
              </div>

              <div className="assignment-actions">
                  <button
                    className="action-link"
                    onClick={() => {
                      setEditingHomework(hw);
                      setShowHomeworkModal(true);
                    }}
                  >
                    {t('common.edit') || 'Bewerken'}
                  </button>
                {hw.attachment && (
                  <>
                    <button
                      className="action-link"
                      onClick={() => handleViewAttachment(hw)}
                    >
                      {t('homework.viewAssignment')}
                    </button>
                    <button className="action-link" onClick={() => handleDownloadAttachment(hw)}>
                      {t('homework.downloadAssignment')}
                    </button>
                  </>
                )}
                <button className="action-link" onClick={() => handleDownloadSubmissionsZip(hw)}>
                  {t('homework.downloadAllSubmissions')}
                </button>
                <button className="load-submissions" onClick={() => fetchSubmissions(hw.id)}>
                  {t('homework.viewSubmissions')}
                </button>
                <button className="load-submissions audit-btn" onClick={() => openAuditLogsModal(hw.id)}>
                  {t('homework.auditLogs')}
                </button>
                <button className="action-link delete-link" onClick={() => handleDeleteHomework(hw)}>
                  {t('common.delete') || 'Verwijderen'}
                </button>
              </div>

              {submissions.length > 0 && (
                <div className="submission-list">
                  {submissions.map(sub => (
                    <div key={sub.id} className="submission-item">
                      <div className="submission-meta">
                        <span className="submission-student">{sub.student.name}</span>
                        <span className="submission-date">{new Date(sub.submittedAt).toLocaleString()}</span>
                        {sub.grade !== undefined && sub.grade !== null && (
                          <span className={`grade-badge ${sub.grade >= 5.5 ? 'passed' : 'failed'}`}>
                            {t('grade.grade')}: {sub.grade}/10
                          </span>
                        )}
                      </div>
                      <div className="submission-actions">
                        {sub.file && (
                          <>
                            <button
                              className="action-link"
                              onClick={() => handleViewSubmission(hw, sub)}
                            >
                              {t('homework.viewSubmission')}
                            </button>
                            <button className="action-link" onClick={() => handleDownloadSubmission(hw, sub)}>
                              {t('homework.downloadSubmission')}
                            </button>
                          </>
                        )}
                        <button 
                          className="action-link grade-link"
                          onClick={() => openGradingModal(sub, hw.id)}
                        >
                          {sub.grade !== undefined && sub.grade !== null ? t('homework.updateGrade') : t('homework.gradeSubmission')}
                        </button>
                      </div>
                      {sub.feedback && (
                        <div className="submission-feedback">
                          <strong>{t('homework.feedback')}:</strong> {sub.feedback}
                        </div>
                      )}
                      {sub.textContent && (
                        <div className="submission-feedback">
                          <strong>Tekstinzending:</strong> {sub.textContent}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {!loading && filteredAssignments.length === 0 && (
          <div className="no-data">{t('common.noData')}</div>
        )}
      </section>

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={pdfModal.isOpen}
        onClose={closePdfModal}
        pdfUrl={pdfModal.url}
        title={pdfModal.title}
      />

      {/* Grading Modal */}
      {gradingModal.isOpen && (
        <div className="grading-modal-overlay" onClick={closeGradingModal}>
          <div className="grading-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="grading-modal-header">
              <h3>{t('homework.gradeSubmission')}</h3>
              <button className="grading-modal-close" onClick={closeGradingModal}>&times;</button>
            </div>
            <form onSubmit={handleGradeSubmit}>
              <div className="grading-modal-body">
                <div className="grading-student-info">
                  <strong>{gradingModal.submission?.student?.name}</strong>
                  <span>{new Date(gradingModal.submission?.submittedAt).toLocaleString()}</span>
                </div>
                <div className="form-row">
                  <label>{t('grade.grade')} (0-10)</label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    step="0.1"
                    value={gradingForm.grade}
                    onChange={(e) => setGradingForm(prev => ({ ...prev, grade: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-row">
                  <label>{t('homework.feedback')}</label>
                  <textarea
                    value={gradingForm.feedback}
                    onChange={(e) => setGradingForm(prev => ({ ...prev, feedback: e.target.value }))}
                    placeholder={t('homework.feedbackPlaceholder')}
                    rows={4}
                  />
                </div>
              </div>
              <div className="grading-modal-footer">
                <button type="button" className="cancel-btn" onClick={closeGradingModal}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="submit-btn">
                  {t('grade.addGrade')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Audit Logs Modal */}
      {auditLogsModal.isOpen && (
        <div className="audit-modal-overlay" onClick={closeAuditLogsModal}>
          <div className="audit-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="audit-modal-header">
              <h3>{t('homework.auditLogs')}</h3>
              <button className="audit-modal-close" onClick={closeAuditLogsModal}>&times;</button>
            </div>
            <div className="audit-modal-body">
              {auditLogsModal.loading ? (
                <div className="loading">{t('common.loading')}</div>
              ) : auditLogsModal.logs.length === 0 ? (
                <div className="no-data">{t('common.noData')}</div>
              ) : (
                <table className="audit-logs-table">
                  <thead>
                    <tr>
                      <th>{t('common.date')}</th>
                      <th>{t('common.name')}</th>
                      <th>{t('homework.action')}</th>
                      <th>{t('homework.target')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogsModal.logs.map(log => (
                      <tr key={log.id}>
                        <td>{new Date(log.createdAt).toLocaleString()}</td>
                        <td>{log.user?.name}</td>
                        <td>{getActionLabel(log.action)}</td>
                        <td>{log.targetType}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHomework;
